import type { PayoutRail } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";

export const PayoutBatchStatus = {
  UPLOADED: "UPLOADED",
  VALIDATING: "VALIDATING",
  VALIDATION_FAILED: "VALIDATION_FAILED",
  READY: "READY",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  EXECUTING: "EXECUTING",
  COMPLETED: "COMPLETED",
  COMPLETED_WITH_ERRORS: "COMPLETED_WITH_ERRORS",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
} as const;
export type PayoutBatchStatus = (typeof PayoutBatchStatus)[keyof typeof PayoutBatchStatus];

export const PayoutBatchItemStatus = {
  PENDING: "PENDING",
  INVALID: "INVALID",
  EXCLUDED: "EXCLUDED",
  SELECTED: "SELECTED",
  QUEUED: "QUEUED",
  PROCESSING: "PROCESSING",
  SUBMITTED: "SUBMITTED",
  PAID: "PAID",
  FAILED: "FAILED",
  REVERSED: "REVERSED",
  CANCELLED: "CANCELLED",
} as const;
export type PayoutBatchItemStatus = (typeof PayoutBatchItemStatus)[keyof typeof PayoutBatchItemStatus];

export interface PayoutBatchCounters {
  total: number;
  pending: number;
  invalid: number;
  excluded: number;
  selected: number;
  queued: number;
  processing: number;
  submitted: number;
  paid: number;
  failed: number;
  reversed: number;
  cancelled: number;
  /** Every row that passed validation, whatever happened to it afterwards. */
  valid: number;
}

/** Decimal strings, 8 dp. `debit_*` are in the source currency, the rest in the delivery currency. */
export interface PayoutBatchTotals {
  receive_selected: string;
  fees_selected: string;
  debit_indicative_selected: string;
  debit_actual: string;
  receive_paid: string;
}

/** A coded reason plus the sentence the backend already localized with `Accept-Language`. */
export interface TranslatedCode {
  code: string;
  field?: string | null;
  params?: Record<string, string>;
  message: string;
}

export const PayoutBatchWarningCode = {
  DUPLICATE_IN_FILE: "duplicate_in_file",
  POSSIBLE_DUPLICATE_PAYOUT: "possible_duplicate_payout",
  POSSIBLE_DUPLICATE_IN_BATCH: "possible_duplicate_in_batch",
  SAME_FILE_UPLOADED_BEFORE: "same_file_uploaded_before",
  IGNORED_COLUMNS: "ignored_columns",
  FEES_UPDATED: "fees_updated",
  BREB_KEYS_UNVERIFIED: "breb_keys_unverified",
} as const;
export type PayoutBatchWarningCode = (typeof PayoutBatchWarningCode)[keyof typeof PayoutBatchWarningCode];

export interface PayoutBatchWarning {
  code: string;
  rows?: number[];
  payout_id?: string | null;
  batch_id?: string | null;
  row_number?: number;
  occurred_at?: string;
  columns?: string[];
  fees_currency?: string;
  debit_currency?: string;
  previous_fees_selected?: string;
  fees_selected?: string;
  previous_debit_selected?: string;
  debit_selected?: string;
  message: string;
}

export type PayoutBatchSuggestionStrategy = "keep_file_order" | "smallest_first" | "largest_first";

export interface PayoutBatchSuggestion {
  strategy: PayoutBatchSuggestionStrategy;
  item_ids: string[];
  count: number;
  total_debit: string;
  remaining_balance: string;
}

export interface PayoutBatchWalletSummary {
  id: string | null;
  available: string;
  pending: string;
  blocked: string;
  status: string | null;
  sufficient: boolean;
  remaining_after: string;
}

export type ProviderBalanceStatus = "OK" | "STALE" | "UNAVAILABLE" | "UNSUPPORTED";

/** INTERNAL staff only. `provider_code` is a wire field and is never rendered (rule 12). */
export interface ProviderBalanceView {
  status: ProviderBalanceStatus;
  provider_code: string | null;
  currency: string;
  available: string | null;
  total: string | null;
  fetched_at: string | null;
  snapshot_id: string | null;
  error_code: string | null;
}

/** Why a member may approve a batch they prepared: their organization allows it, or nobody else can approve. */
export type SelfApprovalReason = "org_setting" | "sole_approver";

/**
 * Who may approve a batch they uploaded or submitted, answered for the viewer. Organization members only:
 * staff always need a second person, and never count as the organization's other approver.
 */
export interface PayoutBatchApprovalPolicy {
  /** The organization's own switch. */
  self_approval_enabled: boolean;
  /** Active members who can approve batches, the viewer and the batch's makers excluded. */
  other_approvers_count: number;
  self_approval_allowed: boolean;
  reason: SelfApprovalReason | null;
}

export interface PayoutBatchSummary {
  counters: PayoutBatchCounters;
  totals: PayoutBatchTotals;
  fx: { rate_indicative: string | null; priced_at: string | null } | null;
  drift_tolerance_pct?: string;
  wallet: PayoutBatchWalletSummary | null;
  /**
   * Echoed verbatim to `submit`, then to `execute` by whoever approves; the server refuses a stale one with
   * 409.
   */
  fingerprint: string | null;
  items_version: number;
  can_submit: boolean;
  /**
   * Fundable, and PENDING_APPROVAL (who approves is decided per request) or READY for a maker who may approve
   * it in one step.
   */
  can_execute: boolean;
  blockers: TranslatedCode[];
  warnings: PayoutBatchWarning[];
  suggestions: PayoutBatchSuggestion[] | null;
  provider_balance: ProviderBalanceView | null;
  /**
   * For the viewer, while money on the batch can still be approved (review, pending approval, retry). Absent
   * from a backend that predates self-approval, which reads as strict dual control.
   */
  approval_policy?: PayoutBatchApprovalPolicy | null;
}

export interface PayoutBatch {
  id: string;
  organization_id: string;
  status: PayoutBatchStatus;
  source_currency: string;
  /** ISO 3166-1 alpha-3. */
  country: string;
  rail: PayoutRail;
  /** Delivery currency. */
  currency: string;
  concept: string | null;
  file_name: string;
  file_size: number;
  row_count: number;
  counters: PayoutBatchCounters;
  totals: PayoutBatchTotals;
  rate_indicative: string | null;
  priced_at: string | null;
  items_version: number;
  expires_at: string | null;
  halt_code: string | null;
  failure_reason: string | null;
  cancel_reason: string | null;
  created_by_user_id: string;
  /** Kept once approved: retry refuses the submitter too. Cleared when the batch returns to review. */
  submitted_by_user_id: string | null;
  approved_by_user_id: string | null;
  /** Approved by whoever prepared it, without a second person. */
  self_approved?: boolean;
  returned_by_user_id: string | null;
  return_reason: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  returned_at: string | null;
  approved_at: string | null;
  execution_started_at: string | null;
  finished_at: string | null;
  last_progress_at: string | null;
}

export interface PayoutBatchDetail extends PayoutBatch {
  summary: PayoutBatchSummary;
}

/** The destination as coloca-nest stores it on the row (`RailDestination`, camelCase). */
export type BatchRailDestination =
  | { rail: "SPEI"; clabe: string; beneficiaryName: string; rfc?: string }
  | {
      rail: "PSE";
      documentType: string;
      documentNumber: string;
      bankCode: string;
      accountType: string;
      accountNumber: string;
    }
  | {
      rail: "PIX";
      pixKeyType: string;
      pixKeyValue: string;
      beneficiaryName?: string;
    }
  | {
      rail: "BREB";
      brebKeyType: string;
      brebKeyValue: string;
      beneficiaryName?: string;
    }
  | {
      rail: "TRANSFIYA";
      accountType: string;
      accountNumber: string;
      bankCode: string;
      documentType: string;
      documentNumber: string;
      beneficiaryName: string;
    }
  | {
      rail: "CBU_CVU";
      accountIdType: string;
      accountIdValue: string;
      cuitCuil?: string;
      beneficiaryName?: string;
    }
  | { rail: "USDT"; network: string; destinationAddr: string };

export type PayoutBatchPricingMode = "SAME_CURRENCY" | "CROSS_CURRENCY";

export interface PayoutBatchItemPricing {
  mode: PayoutBatchPricingMode;
  receive_net: string;
  receive_gross: string;
  fee_total: string;
  coloca_total: string;
  provider_total: string;
  economic_model: string;
  source_currency: string;
  total_debit_source: string;
  conversion_fee_total: string;
  rate: string | null;
  /** Null for members; never rendered for anyone (rule 12). */
  rate_provider_code: string | null;
  priced_at: string;
}

export const PayoutBatchDestinationCheckStatus = {
  FOUND: "found",
  NOT_FOUND: "not_found",
  INACTIVE: "inactive",
  UNAVAILABLE: "unavailable",
} as const;
export type PayoutBatchDestinationCheckStatus =
  (typeof PayoutBatchDestinationCheckStatus)[keyof typeof PayoutBatchDestinationCheckStatus];

/** What the Bre-B directory answered about the row's key during validation. Review only: never sent anywhere. */
export interface PayoutBatchDestinationCheck {
  status: PayoutBatchDestinationCheckStatus;
  /** As the directory reports it, possibly masked. */
  holder_name: string | null;
  bank_name: string | null;
  checked_at: string;
}

export interface PayoutBatchItem {
  id: string;
  batch_id: string;
  row_number: number;
  status: PayoutBatchItemStatus;
  external_reference: string | null;
  beneficiary_name: string | null;
  destination: BatchRailDestination | null;
  /** Bre-B rows only; absent from a backend that predates the check. */
  destination_check?: PayoutBatchDestinationCheck | null;
  receive_amount: string | null;
  concept: string | null;
  pricing: PayoutBatchItemPricing | null;
  errors: TranslatedCode[];
  warnings: PayoutBatchWarning[];
  payout_id: string | null;
  attempt: number;
  failure_code: string | null;
  failure_message: string | null;
  processed_at: string | null;
  raw_row: Record<string, string | number | null>;
  created_at: string;
  updated_at: string;
}

export type PayoutBatchActorType = "USER" | "SYSTEM" | "PROVIDER_WEBHOOK";

export interface PayoutBatchEvent {
  id: string;
  item_id: string | null;
  event_type: string;
  actor_type: PayoutBatchActorType;
  /** Null for members when the actor is Coloca staff. */
  actor_user_id: string | null;
  actor_user_type: "INTERNAL" | "ORGANIZATION_MEMBER" | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export type PayoutBatchStats = Record<PayoutBatchStatus, number>;

export type PayoutBatchSelectionOp =
  | "select"
  | "deselect"
  | "replace"
  | "select_all_valid"
  | "deselect_all"
  | "exclude_with_warnings";

export interface RetryPreviewItem {
  id: string;
  row_number: number;
  pricing: PayoutBatchItemPricing | null;
  error: TranslatedCode | null;
}

export interface RetryPreview {
  batch_id: string;
  source_currency: string;
  currency: string;
  items: RetryPreviewItem[];
  retryable_count: number;
  /** Source-currency sum of the priceable rows — what `retryItems` receives as `maxTotalDebit`. */
  total_debit: string;
}

export interface UploadPayoutBatchInput {
  file: File;
  sourceCurrency: string;
  country: string;
  rail: string;
  concept?: string;
  /** INTERNAL staff only; members never send it (the server pins their organization). */
  organizationId?: string;
}

export interface ListPayoutBatchesParams {
  skip: number;
  limit: number;
  status?: PayoutBatchStatus;
  organizationId?: string;
  rail?: string;
  country?: string;
  from?: string;
  to?: string;
}

export interface ListPayoutBatchItemsParams {
  skip: number;
  limit: number;
  status?: PayoutBatchItemStatus;
  hasErrors?: boolean;
  hasWarnings?: boolean;
}

export interface PaginatedPage<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}
